import { authFetch } from "@/auth/authFetch";
import { apiBase } from "./config";
import type {
  Domain, DomainMember, EligibleMember, EvalRun, PolicyContent, PolicyDetail, PolicyDiff,
  PolicyCoverage, PolicySummary, PolicyVersion, PublishResult,
  ToolCatalog, ScopeDefinition,
} from "./types";

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
  get forbidden() { return this.status === 403; }
}

const seg = encodeURIComponent;
const root = () => `${apiBase()}/v1/ms-bible`;

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  const res = await authFetch(`${root()}${path}`, { ...init, headers });
  const text = await res.text();
  const body = text ? safeJson(text) : undefined;
  if (!res.ok) {
    const message = typeof body === "object" && body && "message" in body ? String((body as { message: unknown }).message) : res.statusText;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text); } catch { return text; }
}

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });
const qs = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
  const s = q.toString();
  return s ? `?${s}` : "";
};

export const api = {
  listPolicies: (f: { domain?: string; state?: string } = {}) => call<PolicySummary[]>(`/policies${qs(f)}`),
  getPolicy: (id: string) => call<PolicyDetail>(`/policies/${seg(id)}`),
  versions: (id: string) => call<PolicyVersion[]>(`/policies/${seg(id)}/versions`),
  diff: (id: string, from: number, to: number) => call<PolicyDiff>(`/policies/${seg(id)}/diff?from=${from}&to=${to}`),
  evals: (id: string) => call<EvalRun[]>(`/policies/${seg(id)}/evals`),
  coverage: (id: string) => call<PolicyCoverage>(`/policies/${seg(id)}/coverage`),
  createPolicy: (content: PolicyContent) => call<PolicyDetail>(`/policies`, json("POST", content)),
  saveDraft: (id: string, content: PolicyContent) => call<PolicyDetail>(`/policies/${seg(id)}/draft`, json("PUT", content)),
  publish: (id: string) => call<PublishResult>(`/policies/${seg(id)}/publish`, { method: "POST" }),
  rollback: (id: string, reason: string) => call<PolicyDetail>(`/policies/${seg(id)}/rollback`, json("POST", { reason })),
  suspend: (id: string, reason: string) => call<PolicyDetail>(`/policies/${seg(id)}/suspend`, json("POST", { reason })),
  resume: (id: string, reason: string) => call<PolicyDetail>(`/policies/${seg(id)}/resume`, json("POST", { reason })),
  listDomains: () => call<Domain[]>(`/domains`),
  createDomain: (key: string, name: string) => call<Domain>(`/domains`, json("POST", { key, name })),
  replaceMembers: (key: string, members: DomainMember[]) => call<DomainMember[]>(`/domains/${seg(key)}/members`, json("PUT", { members })),
  eligibleMembers: () => call<EligibleMember[]>(`/domains/eligible-members`),
  tools: () => call<ToolCatalog>(`/tools`),
  scopes: () => call<ScopeDefinition[]>(`/scopes`),
};
